import { makeAutoObservable, runInAction } from 'mobx'
import { apiClient } from '@/services/api'
import type { Crew, CrewInvokeResponse } from '@/types'

export type CrewProcessType = 'sequential' | 'parallel' | 'hierarchical' | 'all'

class CrewStore {
  // Data
  crews: Crew[] = []
  selectedCrew: Crew | null = null

  // Loading states
  isLoading = false
  isInvoking = false
  error: string | null = null

  // Filters
  domainFilter = 'all'
  processTypeFilter: CrewProcessType = 'all'
  searchQuery = ''
  showDisabledCrews = true

  // Execution tracking
  lastExecution: CrewInvokeResponse | null = null
  executionHistory: Array<{
    crew: string
    request: { prompt: string; context: Record<string, any> }
    response: CrewInvokeResponse
    timestamp: Date
  }> = []

  constructor() {
    makeAutoObservable(this)
  }

  // ==================== Computed Values ====================

  get filteredCrews(): Crew[] {
    return this.crews.filter((crew) => {
      // Domain filter
      if (this.domainFilter !== 'all' && crew.domain !== this.domainFilter) {
        return false
      }

      // Process type filter
      if (this.processTypeFilter !== 'all' && crew.process_type !== this.processTypeFilter) {
        return false
      }

      // Enabled filter
      if (!this.showDisabledCrews && !crew.enabled) {
        return false
      }

      // Search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        const matchesName = crew.name.toLowerCase().includes(query)
        const matchesDisplayName = crew.display_name.toLowerCase().includes(query)
        const matchesDescription = crew.description.toLowerCase().includes(query)
        if (!matchesName && !matchesDisplayName && !matchesDescription) {
          return false
        }
      }

      return true
    })
  }

  get domains(): string[] {
    const domains = new Set(this.crews.map((c) => c.domain))
    return Array.from(domains).sort()
  }

  get hasActiveFilters(): boolean {
    return (
      this.domainFilter !== 'all' ||
      this.processTypeFilter !== 'all' ||
      this.searchQuery !== '' ||
      !this.showDisabledCrews
    )
  }

  get enabledCrewCount(): number {
    return this.crews.filter((c) => c.enabled).length
  }

  get totalAgentCount(): number {
    return this.crews.reduce((sum, crew) => sum + crew.agents.length, 0)
  }

  // ==================== Actions ====================

  async fetchCrews(domain?: string) {
    this.isLoading = true
    this.error = null

    try {
      const crews = await apiClient.getCrews(domain ? { domain } : undefined)

      runInAction(() => {
        this.crews = crews
        this.isLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.crews = []
        this.error = error.response?.data?.detail || 'Failed to fetch crews'
        this.isLoading = false
      })
    }
  }

  async fetchCrew(name: string) {
    this.isLoading = true
    this.error = null

    try {
      const crew = await apiClient.getCrew(name)

      runInAction(() => {
        this.selectedCrew = crew
        this.isLoading = false
      })

      return crew
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to fetch crew'
        this.isLoading = false
      })
      throw error
    }
  }

  async invokeCrew(
    name: string,
    prompt: string,
    context: Record<string, any> = {}
  ): Promise<CrewInvokeResponse> {
    this.isInvoking = true
    this.error = null

    try {
      const response = await apiClient.invokeCrew(name, { prompt, context })

      runInAction(() => {
        this.lastExecution = response
        this.executionHistory.unshift({
          crew: name,
          request: { prompt, context },
          response,
          timestamp: new Date(),
        })
        // Keep only last 50 executions
        if (this.executionHistory.length > 50) {
          this.executionHistory = this.executionHistory.slice(0, 50)
        }
        this.isInvoking = false
      })

      return response
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to invoke crew'
        this.isInvoking = false
      })
      throw error
    }
  }

  async enableCrew(name: string) {
    this.isLoading = true
    this.error = null

    try {
      await apiClient.enableCrew(name)

      runInAction(() => {
        const crew = this.crews.find((c) => c.name === name)
        if (crew) {
          crew.enabled = true
        }
        if (this.selectedCrew?.name === name) {
          this.selectedCrew.enabled = true
        }
        this.isLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to enable crew'
        this.isLoading = false
      })
      throw error
    }
  }

  async disableCrew(name: string) {
    this.isLoading = true
    this.error = null

    try {
      await apiClient.disableCrew(name)

      runInAction(() => {
        const crew = this.crews.find((c) => c.name === name)
        if (crew) {
          crew.enabled = false
        }
        if (this.selectedCrew?.name === name) {
          this.selectedCrew.enabled = false
        }
        this.isLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to disable crew'
        this.isLoading = false
      })
      throw error
    }
  }

  // ==================== Filter Actions ====================

  setDomainFilter(domain: string) {
    this.domainFilter = domain
  }

  setProcessTypeFilter(processType: CrewProcessType) {
    this.processTypeFilter = processType
  }

  setSearchQuery(query: string) {
    this.searchQuery = query
  }

  setShowDisabledCrews(show: boolean) {
    this.showDisabledCrews = show
  }

  clearFilters() {
    this.domainFilter = 'all'
    this.processTypeFilter = 'all'
    this.searchQuery = ''
    this.showDisabledCrews = true
  }

  // ==================== Utility Actions ====================

  clearError() {
    this.error = null
  }

  clearSelectedCrew() {
    this.selectedCrew = null
  }

  clearLastExecution() {
    this.lastExecution = null
  }

  clearExecutionHistory() {
    this.executionHistory = []
  }

  getCrewByName(name: string): Crew | undefined {
    return this.crews.find((c) => c.name === name)
  }

  getCrewsByDomain(domain: string): Crew[] {
    return this.crews.filter((c) => c.domain === domain)
  }

  getExecutionHistoryForCrew(crewName: string) {
    return this.executionHistory.filter((e) => e.crew === crewName)
  }
}

export const crewStore = new CrewStore()
